import { DEFAULT_HEIGHT, DEFAULT_PERIOD, DEFAULT_WIDTH } from '../defaults';
import type { DimensionsValue, MarketSelectField, UtmValue, WidgetConfigState } from '../fields';
import type { EmbedConfig, WidgetSchema } from '../types';

/**
 * Reduce a widget schema + the user's config state into the normalized
 * `EmbedConfig` consumed by both the embed-code generator (./buildEmbedCode) and
 * the live preview (../preview/WidgetPreview). Pure: no React, no `window`.
 *
 * Field keys are conventional and map straight onto EmbedConfig:
 *   event-search -> eventTicker, theme -> colorTheme, dimensions -> width/height,
 *   market-select(single) -> marketId, market-select(multi|orderable) -> marketIds,
 *   category-select -> category, color -> logoColor,
 *   boolean(showLogo|useMostRecentInSeries), enum(variant|period|seriesTicker), utm -> utm*.
 */
export function buildEmbedConfig(schema: WidgetSchema, config: WidgetConfigState): EmbedConfig {
    const embed: EmbedConfig = {
        eventTicker: '',
        widgetSize: schema.embed.fixed.widgetSize ?? 'large',
        variant: schema.embed.fixed.variant,
        colorTheme: 'light',
        width: DEFAULT_WIDTH,
        height: DEFAULT_HEIGHT,
        period: schema.embed.fixed.period ?? DEFAULT_PERIOD,
    };

    const valueOf = (key: string, fallback: unknown) => (config[key] !== undefined ? config[key] : fallback);

    for (const field of schema.fields) {
        const value = valueOf(field.key, field.defaultValue);

        switch (field.kind) {
            case 'event-search':
                embed.eventTicker = String(value ?? '');
                break;
            case 'theme':
                embed.colorTheme = value === 'dark' ? 'dark' : 'light';
                break;
            case 'dimensions': {
                const dims = (value ?? {}) as Partial<DimensionsValue>;
                const fixed = (field as { fixed?: Partial<DimensionsValue> }).fixed ?? {};
                if (fixed.width || dims.width) embed.width = fixed.width ?? dims.width!;
                if (fixed.height || dims.height) embed.height = fixed.height ?? dims.height!;
                break;
            }
            case 'market-select': {
                const ids = (Array.isArray(value) ? value : []) as string[];
                if ((field as MarketSelectField).mode === 'single') {
                    if (ids[0]) embed.marketId = ids[0];
                } else if (ids.length > 0) {
                    embed.marketIds = ids.join(',');
                }
                break;
            }
            case 'category-select':
                embed.category = String(value ?? '') || undefined;
                break;
            case 'color':
                embed.logoColor = String(value ?? '') || undefined;
                break;
            case 'boolean':
                if (field.key === 'showLogo') embed.showLogo = Boolean(value);
                else if (field.key === 'useMostRecentInSeries') embed.useMostRecentInSeries = Boolean(value);
                else if (field.key === 'showBorder') embed.showBorder = Boolean(value);
                else if (field.key === 'rounded') embed.rounded = Boolean(value);
                break;
            case 'enum':
                if (field.key === 'variant') embed.variant = String(value ?? '') || embed.variant;
                else if (field.key === 'period') embed.period = String(value ?? '') || embed.period;
                else if (field.key === 'seriesTicker') embed.seriesTicker = String(value ?? '') || undefined;
                // Any other enum is a widget-specific choice (e.g. which GPU chip). Forward
                // it as a query param so direct-iframe widgets can expose a picker.
                else if (String(value ?? '')) {
                    embed.iframeParams = { ...embed.iframeParams, [field.key]: String(value) };
                }
                break;
            case 'utm': {
                const utm = (value ?? {}) as Partial<UtmValue>;
                if (utm.utmSource) embed.utmSource = utm.utmSource;
                if (utm.utmCampaign) embed.utmCampaign = utm.utmCampaign;
                if (utm.promoCode) embed.promoCode = utm.promoCode;
                break;
            }
        }
    }

    return embed;
}
